import axios from 'axios';
import moment from 'moment';

// Simple expiration check functions
const isOfferExpired = (offer) => {
  if (!offer?.expiresOn) return false;
  return moment().isAfter(moment(offer.expiresOn));
};

const isSearchExpired = (searchExpiresOn) => {
  if (!searchExpiresOn) return false;
  return moment().isAfter(moment(searchExpiresOn));
};

// API Base URL - Use Lambda endpoints to avoid CORS issues
const API_BASE_URL = "https://0nc70p8y1i.execute-api.eu-central-1.amazonaws.com";

// TourVisio Credentials
const TOURVISIO_CREDENTIALS = {
  Agency: "PXM109476",
  User: "USR163895",
  Password: "K0205*"
};

let token = null;            // Store token globally
let tokenExpiry = null;      // Store expiry time
let authPromise = null;      // Prevent duplicate auth calls

// Authenticate and Get Token (Only Once)
export const authenticateTourVisio = async () => {
  if (token && tokenExpiry && moment().isBefore(tokenExpiry)) {
    return token;
  }

  if (!authPromise) {
    authPromise = axios
      .post(`https://0nc70p8y1i.execute-api.eu-central-1.amazonaws.com/paximum/authenticate`)
      .then((response) => {
        
        // Handle Lambda response structure: response.data.data.body.token
        const lambdaToken = response.data?.data?.body?.token || response.data?.token;
        const lambdaExpiry = response.data?.data?.body?.expiresOn;
        
        if (lambdaToken) {
          token = lambdaToken;
          if (lambdaExpiry) {
            tokenExpiry = moment(lambdaExpiry);
          } else {
            // Default to 2 hours if no expiry provided
            tokenExpiry = moment().add(2, 'hours');
          }
          return token;
        }
        
        console.error("❌ No token found in Lambda response:", response.data);
        throw new Error("Failed to obtain token from Lambda - no token in response");
      })
      .catch((error) => {
        console.error("❌ Lambda Authentication Error:", error.response?.data || error.message);
        token = null;
        tokenExpiry = null;
        throw error;
      })
      .finally(() => {
        authPromise = null;
      });
  }

  return authPromise;
};

// ✅ NEW: Validate offer expiration before booking
const validateOfferExpiration = (offerIds, offersData = []) => {
  const issues = [];
  
  offerIds.forEach(offerId => {
    const offer = offersData.find(o => o.offerId === offerId);
    if (offer && isOfferExpired(offer)) {
      const expiryTime = moment(offer.expiresOn).format('YYYY-MM-DD HH:mm:ss');
      issues.push(`Offer ${offerId} expired at ${expiryTime}`);
    }
  });
  
  return issues;
};

// Funksioni për të bërë commit të transaksionit
export const commitPaximumTransaction = async (transactionId) => {
  try {
    const token = await authenticateTourVisio();
  
    
    const response = await axios.post(
      `${API_BASE_URL}/paximum/committransaction`,
      { transactionId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const success = response.data?.header?.success || false;
    
    if (!success) {
      console.error('❌ Transaction commit failed:', response.data?.header?.messages);
    }

    return {
      success,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error committing transaction:', error.response?.data || error.message);
    return {
      success: false,
      error: error.message,
      details: error.response?.data
    };
  }
};

// ✅ Enhanced beginPaximumTransaction with expiration validation
export const beginPaximumTransaction = async (offerIds, currency = "EUR", offersData = [], searchExpiresOn = null) => {
  try {
    // ✅ Validate search expiration
    if (searchExpiresOn && isSearchExpired(searchExpiresOn)) {
      const expiryTime = moment(searchExpiresOn).format('YYYY-MM-DD HH:mm:ss');
      throw new Error(`Search expired at ${expiryTime}. Please refresh and search again.`);
    }
    
    // ✅ Validate offer expiration
    const expirationIssues = validateOfferExpiration(offerIds, offersData);
    if (expirationIssues.length > 0) {
      throw new Error(`Offers expired: ${expirationIssues.join(', ')}. Please refresh and select new offers.`);
    }

    const token = await authenticateTourVisio();

    const response = await axios.post(
      `${API_BASE_URL}/paximum/begintransaction`,
      { 
        offerIds,
        currency,
        culture: "en-US"
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const success = response.data?.header?.success || false;
    const transactionId = response.data?.body?.transactionId;
    const transactionExpiresOn = response.data?.body?.expiresOn;
    
    
    if (transactionExpiresOn) {
      const timeToExpiry = moment(transactionExpiresOn).diff(moment(), 'minutes');
      if (timeToExpiry < 15) {
        console.warn(`⚠️ Transaction expires in ${timeToExpiry} minutes at ${transactionExpiresOn}`);
      }
    }

    return {
      success,
      transactionId,
      expiresOn: transactionExpiresOn,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error beginning transaction:', error.message);
    return {
      success: false,
      error: error.message,
      details: error.response?.data
    };
  }
};

// ✅ Enhanced setReservationInfo with transaction expiration check
export const setReservationInfo = async (transactionId, travellers, reservationNote = "", companyInfo = null, transactionExpiresOn = null) => {
  try {
    // ✅ Check if transaction has expired
    if (transactionExpiresOn && moment().isAfter(moment(transactionExpiresOn))) {
      throw new Error(`Transaction expired at ${moment(transactionExpiresOn).format('YYYY-MM-DD HH:mm:ss')}. Please start a new booking.`);
    }

    const token = await authenticateTourVisio();


    // Build the request payload
    const payload = {
      transactionId,
      travellers,
      reservationNote: reservationNote || "Booked via website"
    };

    // Add company information if provided
    if (companyInfo && companyInfo.companyName && companyInfo.taxNumber) {
      payload.customerInfo = {
        isCompany: true,
        title: 1, // Default title
        name: companyInfo.companyName,
        surname: "", // Company doesn't have surname
        birthDate: "1990-01-01", // Default date for company
        identityNumber: companyInfo.taxNumber,
        passportInfo: {},
        address: {
          city: {
            name: companyInfo.companyCity || ""
          },
          country: {
            name: companyInfo.companyCountry?.name || ""
          },
          email: travellers.find(t => t.isLeader)?.address?.email || "",
          phone: travellers.find(t => t.isLeader)?.address?.contactPhone?.phoneNumber || "",
          address: companyInfo.companyAddress || "",
          zipCode: companyInfo.companyZipCode || ""
        },
        taxInfo: {
          taxOffice: companyInfo.taxOffice || "",
          taxNumber: companyInfo.taxNumber
        }
      };
    }

    const response = await axios.post(
      `${API_BASE_URL}/paximum/setreservationinfo`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const success = response.data?.header?.success || false;

    return {
      success,
      data: response.data
    };
  } catch (error) {
    console.error('❌ Error setting reservation info:', error.message);
    return {
      success: false,
      error: error.message,
      details: error.response?.data
    };
  }
}; 